import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ReactComponent as ArrowsUpDownIcon } from '@assets/images/Input/arrows-up-down.svg'
import { ReactComponent as MembershipMark } from '@assets/images/Navigation/membership.svg'
import { ReactComponent as CloseIcon } from '@assets/images/Icons/Inclusion_close.svg'
import { ReactComponent as CheckmarkCircleIcon } from '@assets/images/Icons/Checkmark_2.svg'
import { ReactComponent as MultiplyCloseIcon } from '@assets/images/Icons/Multiply_close.svg'
import { ReactComponent as SelectCheckIcon } from '@assets/images/Icons/Select_check.svg'
import { ReactComponent as ClockIcon } from '@assets/images/Icons/Clock.svg'
import { ReactComponent as AddIcon } from '@assets/images/Icons/Inclusion_add.svg'
import { ReactComponent as SubtractIcon } from '@assets/images/Icons/Inclusion_subtract.svg'
import { ReactComponent as EditIcon } from '@assets/images/Icons/Edit.svg'
import {
  ActionButtonIcons,
  ActionButtons,
  Badge,
  Button,
  DEFAULT_EMAIL_FAX_TOOLBAR,
  InputField,
  RichTextEditor,
  Select,
  ToggleSwitch,
  type InputFieldVariant,
} from '@enamelai/design-system'
import { Icon as DeleteFilledIcon } from '@enamelai/design-system/icons/Delete2'
import { useNotificationToast } from '@context/NotificationToastContext'
import { useTheme } from '@context/ThemeContext'
import { useSettingsHeader } from '@pages/Settings/SettingsHeader.context'
import { MembershipPerk, MembershipTier } from '@dataTypes/membership.type'
import {
  createEmptyMembershipTierForm,
  createMembershipInclusion,
  createMembershipPerkId,
  MEMBERSHIP_TIER_COLOR_OPTIONS,
  MembershipTierFormData,
  perksToBenefits,
  tierToFormData,
  tierToPerks,
} from './membershipSettings.constants'
import { createFailedToast } from '@utils/notificationToast.util'
import '@components/pages/Settings/SettingsStyle.css'
import './MembershipStyle.css'
import './AddMembershipTierStyle.css'
import AttributeProcedureCodesModal, {
  AttributedProcedureOption,
} from './AttributeProcedureCodesModal.component'
import DeleteMembershipConfirmModal from './DeleteMembershipConfirmModal.component'

type AddMembershipTierProps = {
  mode: 'add' | 'edit'
  initialTier?: MembershipTier
  onCancel: () => void
  onSave: (
    tier: MembershipTier,
    perks: MembershipPerk[],
  ) => void | Promise<void>
  onDelete?: () => void | Promise<void>
}

const INCLUSION_TYPE_OPTIONS = [
  { value: 'freebie', label: 'Freebie' },
  { value: 'discount', label: 'Discount' },
]

const EMAIL_ONBOARDING_MESSAGE = [
  '<p>Hello <strong style="color:#185dd8">[patient name],</strong></p>',
  '<p>Welcome to your <strong style="color:#185dd8">Basic </strong>membership! We are excited to have you as part of our dental family.</p>',
  '<p><strong>Inclusions:</strong></p>',
  '<ul><li>1 Free cleaning/yr</li><li>1 Free exam/yr</li><li>5% off all procedures</li></ul>',
].join('')

const SMS_ONBOARDING_MESSAGE = [
  '<p>Hi <strong style="color:#185dd8">[patient name]</strong>, your <strong style="color:#185dd8">Basic</strong> memberships is now ACTIVE!</p>',
  '<p>Inclusions:</p>',
  '<p>1 Free cleaning/yr</p>',
  '<p>1 Free exam/yr</p>',
  '<p>5% off all procedures</p>',
].join('')

const PLAIN_TEXT_VARIANT = 'text' as InputFieldVariant

const parsePrice = (value: string): number | null => {
  const parsed = Number.parseFloat(value.replace(/,/g, ''))
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : null
}

const CLEANING_PROCEDURE_OPTIONS = [
  { code: 'C1234', description: 'cleanings' },
  {
    code: 'D9934',
    description:
      'cleaning and inspection of removable partial denture, maxillary',
  },
  {
    code: 'D6080',
    description:
      'implant maintenance procedures when prostheses are removed and reinserted, including cleansing of prostheses and abutments',
  },
  {
    code: 'D9933',
    description:
      'cleaning and inspection of removable complete denture, mandibular',
  },
  {
    code: 'D6101',
    description:
      'debridement of a peri-implant defect or defects surrounding a single implant, and surface cleaning of the exposed implant surfaces',
  },
  {
    code: 'D9932',
    description:
      'cleaning and inspection of removable complete denture, maxillary',
  },
  {
    code: 'D9935',
    description:
      'cleaning and inspection of removable partial denture, mandibular',
  },
  {
    code: 'D5993',
    description:
      'maintenance and cleaning of a maxillofacial prosthesis (extra- or intra-oral) other than required adjustments',
  },
  {
    code: 'D6102',
    description:
      'debridement and osseous contouring of a peri-implant defect or defects surrounding a single implant',
  },
  {
    code: 'D6081',
    description:
      'scaling and debridement in the presence of inflammation or mucositis of a single implant, including cleansing of the implant surfaces',
  },
]

const EXAM_PROCEDURE_OPTIONS = [
  {
    code: 'D0486',
    description:
      'laboratory accession of transepithelial cytologic sample, microscopic examination, preparation and transmission of written report',
  },
  {
    code: 'D0473',
    description:
      'accession of tissue, gross and microscopic examination, preparation and transmission of written report',
  },
  {
    code: 'D0120',
    description: 'periodic oral evaluation - established patient',
  },
  {
    code: 'D0150',
    description: 'comprehensive oral evaluation - new or established patient',
  },
  {
    code: 'D0140',
    description: 'limited oral evaluation - problem focused',
  },
  {
    code: 'D0210',
    description: 'intraoral - complete series of radiographic images',
  },
]

const INCLUSION_COLUMNS = [
  { id: 'label', label: 'Perks & benefits', sortable: true },
  { id: 'type', label: 'Type', sortable: true },
  { id: 'usageLimit', label: 'Usage Limit', sortable: true },
  { id: 'amount', label: 'Discount', sortable: true },
  { id: 'procedures', label: 'Attributed procedures', sortable: true },
  { id: 'action', label: 'Action', sortable: false },
] as const

type InclusionSortColumn = Exclude<
  (typeof INCLUSION_COLUMNS)[number]['id'],
  'action'
>

const sampleInclusions = (): MembershipPerk[] => [
  {
    id: createMembershipPerkId(),
    label: 'Free cleaning/yr',
    amount: '100',
    type: 'freebie',
    usageLimit: 1,
    procedures: ['C1234', 'D9934'],
    extraProcedureCount: 8,
    procedureOptions: CLEANING_PROCEDURE_OPTIONS,
    selectedProcedureCode: 'D6081',
  },
  {
    id: createMembershipPerkId(),
    label: 'Free exam/yr',
    amount: '100',
    type: 'freebie',
    usageLimit: 1,
    procedures: ['D0486', 'D0473'],
    extraProcedureCount: 4,
    procedureOptions: EXAM_PROCEDURE_OPTIONS,
  },
  {
    id: createMembershipPerkId(),
    label: '5% off all procedures',
    amount: '5',
    type: 'discount',
    usageLimit: null,
    procedures: ['All procedures'],
  },
]

const perkProcedureCodes = (perk: MembershipPerk): string[] => {
  if (perk.procedures?.length) return perk.procedures
  if (perk.procedure) return [perk.procedure]
  return []
}

const attributedProcedureOptions = (
  perk: MembershipPerk,
): AttributedProcedureOption[] => {
  if (perk.procedureOptions?.length) return perk.procedureOptions
  return perkProcedureCodes(perk).map((code) => ({ code, description: '' }))
}

const inclusionSortValue = (
  perk: MembershipPerk,
  columnId: InclusionSortColumn,
): string | number => {
  if (columnId === 'type') return perk.type ?? 'freebie'
  if (columnId === 'usageLimit') {
    return perk.usageLimit ?? Number.MAX_SAFE_INTEGER
  }
  if (columnId === 'amount') {
    const parsed = Number.parseFloat(perk.amount)
    return Number.isFinite(parsed) ? parsed : Number.MAX_SAFE_INTEGER
  }
  if (columnId === 'procedures') return perkProcedureCodes(perk).join(', ')
  return perk.label.trim().toLowerCase()
}

const compareInclusionValues = (
  left: string | number,
  right: string | number,
): number => {
  if (typeof left === 'number' && typeof right === 'number') return left - right
  return String(left).localeCompare(String(right), undefined, {
    numeric: true,
    sensitivity: 'base',
  })
}

const AddMembershipTier = ({
  mode,
  initialTier,
  onCancel,
  onSave,
  onDelete,
}: AddMembershipTierProps): React.ReactElement => {
  const { setHeaderButtons, setHeaderTitle } = useSettingsHeader()
  const { showToast } = useNotificationToast()
  const { resolvedTheme } = useTheme()
  const badgeMode = resolvedTheme === 'dark' ? 'solid' : 'tinted'

  const [form, setForm] = useState<MembershipTierFormData>(() =>
    initialTier
      ? tierToFormData(initialTier)
      : {
          ...createEmptyMembershipTierForm(),
          name: 'Basic',
          monthlyPrice: '9.99',
          annualPrice: '99.99',
          tierColor: 'basic',
        },
  )
  const [limitMembers, setLimitMembers] = useState(
    () => !initialTier || Boolean(initialTier.maxMembers),
  )
  const [perks, setPerks] = useState<MembershipPerk[]>(() =>
    initialTier ? tierToPerks(initialTier) : sampleInclusions(),
  )
  const [inclusionSortColumn, setInclusionSortColumn] =
    useState<InclusionSortColumn | null>(null)
  const [inclusionSortDirection, setInclusionSortDirection] = useState<
    'asc' | 'desc'
  >('asc')
  const [emailMessage, setEmailMessage] = useState(
    initialTier?.emailOnboardingMessage || EMAIL_ONBOARDING_MESSAGE,
  )
  const [smsMessage, setSmsMessage] = useState(SMS_ONBOARDING_MESSAGE)
  const [editingMessage, setEditingMessage] = useState<'email' | 'sms' | null>(
    null,
  )
  const emailSnapshot = useRef(EMAIL_ONBOARDING_MESSAGE)
  const smsSnapshot = useRef(SMS_ONBOARDING_MESSAGE)
  const [errors, setErrors] = useState<
    Partial<Record<keyof MembershipTierFormData, string>>
  >({})
  const [openProcedurePerkId, setOpenProcedurePerkId] = useState<string | null>(
    null,
  )
  const [attributePerkId, setAttributePerkId] = useState<string | null>(null)
  const [perkToDelete, setPerkToDelete] = useState<MembershipPerk | null>(null)
  const [confirmDeleteTier, setConfirmDeleteTier] = useState(false)
  const [procedureMenuPosition, setProcedureMenuPosition] = useState({
    top: 0,
    left: 0,
  })
  const procedureMenuRef = useRef<HTMLDivElement>(null)

  const openProcedurePerk = perks.find(
    (perk) => perk.id === openProcedurePerkId,
  )

  const headerLabel = mode === 'add' ? 'Add tier' : 'Edit tier'

  const sortedPerks = useMemo(() => {
    if (!inclusionSortColumn) return perks
    const direction = inclusionSortDirection === 'asc' ? 1 : -1
    return [...perks].sort(
      (left, right) =>
        compareInclusionValues(
          inclusionSortValue(left, inclusionSortColumn),
          inclusionSortValue(right, inclusionSortColumn),
        ) * direction,
    )
  }, [inclusionSortColumn, inclusionSortDirection, perks])

  const toggleInclusionSort = (columnId: InclusionSortColumn): void => {
    setInclusionSortColumn((current) => {
      if (current === columnId) {
        setInclusionSortDirection((direction) =>
          direction === 'asc' ? 'desc' : 'asc',
        )
        return current
      }

      setInclusionSortDirection('asc')
      return columnId
    })
  }

  const handleFormChange = (
    field: keyof MembershipTierFormData,
    value: string,
  ): void => {
    setForm((prev) => ({ ...prev, [field]: value }))
    setErrors((prev) => ({ ...prev, [field]: undefined }))
  }

  const updatePerk = useCallback(
    (perkId: string, patch: Partial<MembershipPerk>): void => {
      setPerks((prev) =>
        prev.map((perk) => (perk.id === perkId ? { ...perk, ...patch } : perk)),
      )
    },
    [],
  )

  const attributePerk = perks.find((perk) => perk.id === attributePerkId)

  const saveAttributedProcedures = (
    next: AttributedProcedureOption[],
  ): void => {
    if (!attributePerk) return
    const visible = next.slice(0, 2).map((item) => item.code)
    updatePerk(attributePerk.id, {
      procedureOptions: next,
      procedures: visible,
      extraProcedureCount:
        Math.max(0, next.length - visible.length) || undefined,
      selectedProcedureCode: next.some(
        (item) => item.code === attributePerk.selectedProcedureCode,
      )
        ? attributePerk.selectedProcedureCode
        : undefined,
    })
    setAttributePerkId(null)
  }

  const openProcedureMenu = (
    perkId: string,
    anchor: HTMLButtonElement,
  ): void => {
    if (openProcedurePerkId === perkId) {
      setOpenProcedurePerkId(null)
      return
    }

    const rect = anchor.getBoundingClientRect()
    const menuWidth = 442
    const left = Math.max(
      12,
      Math.min(rect.left, window.innerWidth - menuWidth - 12),
    )
    setProcedureMenuPosition({ top: rect.bottom + 4, left })
    setOpenProcedurePerkId(perkId)
  }

  useEffect(() => {
    if (!openProcedurePerkId) return

    const handlePointerDown = (event: MouseEvent): void => {
      const target = event.target as HTMLElement
      if (procedureMenuRef.current?.contains(target)) return
      if (target.closest('.membership-add-tier-more')) return
      setOpenProcedurePerkId(null)
    }

    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') setOpenProcedurePerkId(null)
    }

    document.addEventListener('mousedown', handlePointerDown)
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('mousedown', handlePointerDown)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [openProcedurePerkId])

  const handleDeletePerk = useCallback((perkId: string): void => {
    setPerks((prev) => prev.filter((perk) => perk.id !== perkId))
    setPerkToDelete(null)
  }, [])

  const confirmDeleteEditingTier = useCallback((): void => {
    setConfirmDeleteTier(false)
    void onDelete?.()
  }, [onDelete])

  const handleSave = useCallback((): void => {
    const nextErrors: Partial<Record<keyof MembershipTierFormData, string>> = {}

    if (!form.name.trim()) {
      nextErrors.name = 'Tier name is required'
    }
    if (parsePrice(form.monthlyPrice) == null) {
      nextErrors.monthlyPrice = 'Enter a valid monthly price'
    }
    if (parsePrice(form.annualPrice) == null) {
      nextErrors.annualPrice = 'Enter a valid annual price'
    }
    if (!form.tierColor) {
      nextErrors.tierColor = 'Select a tier color'
    }
    if (
      limitMembers &&
      form.maxMembers.trim() &&
      Number.isNaN(Number(form.maxMembers))
    ) {
      nextErrors.maxMembers = 'Enter a valid number'
    }

    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors)
      showToast(
        createFailedToast(
          'Unable to save',
          'Please fix the highlighted fields and try again.',
        ),
      )
      return
    }

    const monthlyPrice = parsePrice(form.monthlyPrice)!
    const annualPrice = parsePrice(form.annualPrice)!
    const maxMembers =
      limitMembers && form.maxMembers.trim()
        ? Number.parseInt(form.maxMembers, 10)
        : null
    const savedPerks = perks.filter((perk) => perk.label.trim())

    const tier: MembershipTier = {
      id: initialTier?.id ?? `tier-${Date.now()}`,
      name: form.name.trim(),
      shieldVariant: form.tierColor as MembershipTier['shieldVariant'],
      monthlyPrice,
      annualPrice,
      enabled: initialTier?.enabled ?? true,
      maxMembers,
      description: form.description.trim() || undefined,
      benefits: perksToBenefits(savedPerks),
      clinicId: initialTier?.clinicId,
    }

    void onSave(tier, savedPerks)
  }, [form, initialTier, limitMembers, onSave, perks, showToast])

  useEffect(() => {
    setHeaderTitle(
      <div className="settings-title-with-back">
        <button
          type="button"
          className="unstyled-button stwb-button"
          onClick={onCancel}
        >
          <span className="stwb-back-icon mgc_left_line" />
          <span className="stwb-text">
            Membership<span>/</span>
          </span>
        </button>
        <span>{headerLabel}</span>
      </div>,
    )
    setHeaderButtons(
      <>
        {mode === 'edit' && onDelete ? (
          <Button
            variant="secondary"
            size="md"
            onClick={() => setConfirmDeleteTier(true)}
          >
            Delete
          </Button>
        ) : null}
        <Button variant="secondary" size="md" onClick={onCancel}>
          Cancel
        </Button>
        <Button variant="primary" size="md" onClick={handleSave}>
          Save changes
        </Button>
      </>,
    )

    return () => {
      setHeaderButtons(null)
      setHeaderTitle(null)
    }
  }, [
    handleSave,
    headerLabel,
    mode,
    onCancel,
    onDelete,
    setHeaderButtons,
    setHeaderTitle,
  ])

  return (
    <div className="membership-add-tier">
      <section className="membership-add-tier-details">
        <h2 className="membership-add-tier-section-title">
          Membership tier details
        </h2>
        <div className="membership-add-tier-colors">
          <span className="membership-add-tier-label">Choose tier color</span>
          <div className="membership-add-tier-color-list">
            {MEMBERSHIP_TIER_COLOR_OPTIONS.map((option) => {
              const selected = form.tierColor === option.value
              return (
                <button
                  key={option.value}
                  type="button"
                  className={`membership-add-tier-color${
                    selected ? ' membership-add-tier-color--selected' : ''
                  }`}
                  aria-pressed={selected}
                  onClick={() => handleFormChange('tierColor', option.value)}
                >
                  <MembershipMark
                    className={`membership-add-tier-shield membership-add-tier-shield--${option.value}`}
                    aria-hidden
                  />
                  {option.label}
                </button>
              )
            })}
          </div>
          {errors.tierColor ? (
            <span className="membership-add-tier-hint">{errors.tierColor}</span>
          ) : null}
        </div>
        <div className="membership-add-tier-fields">
          <InputField
            className={`membership-add-tier-detail-input${
              errors.name ? ' membership-add-tier-input--error' : ''
            }`}
            variant={PLAIN_TEXT_VARIANT}
            label="Tier name"
            value={form.name}
            onChange={(e) => handleFormChange('name', e.target.value)}
            onClear={() => handleFormChange('name', '')}
            helperText={errors.name}
          />
          <InputField
            className={`membership-add-tier-price membership-add-tier-detail-input${
              errors.monthlyPrice ? ' membership-add-tier-input--error' : ''
            }`}
            variant="amount"
            label="Monthly price"
            value={form.monthlyPrice}
            onChange={(e) => handleFormChange('monthlyPrice', e.target.value)}
            helperText={errors.monthlyPrice}
          />
          <InputField
            className={`membership-add-tier-price membership-add-tier-detail-input${
              errors.annualPrice ? ' membership-add-tier-input--error' : ''
            }`}
            variant="amount"
            label="Annual price"
            value={form.annualPrice}
            onChange={(e) => handleFormChange('annualPrice', e.target.value)}
            helperText={errors.annualPrice}
          />
          <InputField
            className="membership-add-tier-detail-input"
            variant={PLAIN_TEXT_VARIANT}
            label="Description"
            placeholder="Write here..."
            value={form.description}
            onChange={(e) => handleFormChange('description', e.target.value)}
            onClear={() => handleFormChange('description', '')}
          />
        </div>
      </section>

      <div className="membership-add-tier-limit">
        <label className="membership-add-tier-limit-toggle">
          <ToggleSwitch
            checked={limitMembers}
            aria-label="Set maximum members"
            onChange={(checked) => {
              setLimitMembers(checked)
              if (!checked) handleFormChange('maxMembers', '')
            }}
          />
          <span>Set maximum members</span>
        </label>
        {limitMembers ? (
          <InputField
            className={`membership-add-tier-limit-input${
              errors.maxMembers ? ' membership-add-tier-input--error' : ''
            }`}
            variant="number"
            numberMin={0}
            placeholder="ex. 20"
            aria-label="Maximum members"
            value={form.maxMembers}
            onChange={(e) => handleFormChange('maxMembers', e.target.value)}
            helperText={errors.maxMembers}
          />
        ) : null}
      </div>

      <section className="membership-add-tier-inclusions">
        <div className="membership-add-tier-inclusions-head">
          <h2 className="membership-add-tier-section-title">
            Setup inclusions
          </h2>
          <Button
            variant="primary"
            size="md"
            onClick={() =>
              setPerks((current) => [...current, createMembershipInclusion()])
            }
          >
            Add inclusion
          </Button>
        </div>
        <div className="membership-add-tier-table" role="table">
          <div
            className="membership-add-tier-table-row membership-add-tier-table-row--head"
            role="row"
          >
            {INCLUSION_COLUMNS.map((column) =>
              column.sortable ? (
                <button
                  key={column.id}
                  type="button"
                  className="membership-add-tier-sort-button"
                  role="columnheader"
                  aria-sort={
                    inclusionSortColumn === column.id
                      ? inclusionSortDirection === 'asc'
                        ? 'ascending'
                        : 'descending'
                      : 'none'
                  }
                  onClick={() =>
                    toggleInclusionSort(column.id as InclusionSortColumn)
                  }
                >
                  {column.label}
                  <ArrowsUpDownIcon
                    className="membership-add-tier-sort"
                    aria-hidden
                  />
                </button>
              ) : (
                <span key={column.id} role="columnheader">
                  {column.label}
                </span>
              ),
            )}
          </div>
          {sortedPerks.map((perk) => (
            <div
              key={perk.id}
              className="membership-add-tier-table-row"
              role="row"
            >
              <InputField
                className="membership-add-tier-cell-input"
                variant={PLAIN_TEXT_VARIANT}
                placeholder="Enter perk"
                aria-label="Perk"
                value={perk.label}
                onChange={(e) => updatePerk(perk.id, { label: e.target.value })}
                onClear={() => updatePerk(perk.id, { label: '' })}
              />
              <Select
                label=""
                placeholder="Type"
                options={INCLUSION_TYPE_OPTIONS}
                value={perk.type ?? 'freebie'}
                onChange={(value) =>
                  updatePerk(perk.id, {
                    type: value === 'discount' ? 'discount' : 'freebie',
                  })
                }
              />
              <div className="membership-add-tier-usage">
                {perk.usageLimit == null ? (
                  <div className="membership-add-tier-usage-field membership-add-tier-usage-field--unlimited">
                    <span>Unlimited</span>
                    <button
                      type="button"
                      aria-label="Clear unlimited usage"
                      onClick={() => updatePerk(perk.id, { usageLimit: 1 })}
                    >
                      <CloseIcon aria-hidden />
                    </button>
                  </div>
                ) : (
                  <>
                    <button
                      type="button"
                      className="membership-add-tier-usage-btn membership-add-tier-usage-btn--minus"
                      aria-label="Decrease usage limit"
                      onClick={() =>
                        updatePerk(perk.id, {
                          usageLimit: Math.max(0, (perk.usageLimit ?? 0) - 1),
                        })
                      }
                    >
                      <SubtractIcon aria-hidden />
                    </button>
                    <div className="membership-add-tier-usage-field">
                      <input
                        aria-label="Usage limit"
                        value={perk.usageLimit || ''}
                        onChange={(event) => {
                          const next = Number.parseInt(event.target.value, 10)
                          updatePerk(perk.id, {
                            usageLimit: Number.isNaN(next)
                              ? 0
                              : Math.max(0, next),
                          })
                        }}
                      />
                      {perk.usageLimit ? (
                        <button
                          type="button"
                          aria-label="Clear usage limit"
                          onClick={() => updatePerk(perk.id, { usageLimit: 0 })}
                        >
                          <CloseIcon aria-hidden />
                        </button>
                      ) : null}
                    </div>
                    <button
                      type="button"
                      className="membership-add-tier-usage-btn"
                      aria-label="Increase usage limit"
                      onClick={() =>
                        updatePerk(perk.id, {
                          usageLimit: (perk.usageLimit ?? 0) + 1,
                        })
                      }
                    >
                      <AddIcon aria-hidden />
                    </button>
                  </>
                )}
                <button
                  type="button"
                  className={`membership-add-tier-usage-btn membership-add-tier-usage-btn--clock${
                    perk.usageLimit == null
                      ? ' membership-add-tier-usage-btn--clock-active'
                      : ''
                  }`}
                  aria-label="Toggle unlimited usage"
                  aria-pressed={perk.usageLimit == null}
                  onClick={() =>
                    updatePerk(perk.id, {
                      usageLimit: perk.usageLimit == null ? 1 : null,
                    })
                  }
                >
                  <ClockIcon aria-hidden />
                </button>
              </div>
              <InputField
                className="membership-add-tier-cell-input"
                variant={PLAIN_TEXT_VARIANT}
                leadingIcon={
                  <span className="membership-add-tier-percent">%</span>
                }
                placeholder="0"
                aria-label="Discount"
                value={perk.amount}
                onChange={(e) =>
                  updatePerk(perk.id, { amount: e.target.value })
                }
                onClear={() => updatePerk(perk.id, { amount: '' })}
              />
              <div className="membership-add-tier-procedures">
                <div className="membership-add-tier-procedures-field">
                  {perkProcedureCodes(perk).map((code) => (
                    <Badge
                      key={code}
                      mode={badgeMode}
                      color="blue"
                      label={code}
                    />
                  ))}
                  {perk.extraProcedureCount ? (
                    <button
                      type="button"
                      className="membership-add-tier-more"
                      aria-expanded={openProcedurePerkId === perk.id}
                      aria-label={`Show ${perk.extraProcedureCount} more procedures`}
                      onClick={(event) =>
                        openProcedureMenu(perk.id, event.currentTarget)
                      }
                    >
                      <Badge
                        mode={badgeMode}
                        color="gray"
                        label={`${perk.extraProcedureCount}+ more`}
                      />
                    </button>
                  ) : null}
                </div>
              </div>
              <ActionButtons
                className="membership-add-tier-actions"
                size="md"
                actions={[
                  {
                    id: 'view',
                    label: 'View',
                    icon: ActionButtonIcons.view(),
                    tone: 'primary',
                    variant: 'solid',
                    onClick: () => setAttributePerkId(perk.id),
                  },
                  {
                    id: 'delete',
                    label: 'Delete',
                    icon: (
                      <DeleteFilledIcon
                        className="ds-action-button__svg"
                        aria-hidden="true"
                      />
                    ),
                    tone: 'danger',
                    variant: 'outline',
                    onClick: () => setPerkToDelete(perk),
                  },
                ]}
              />
            </div>
          ))}
        </div>
      </section>

      {openProcedurePerk?.procedureOptions?.length ? (
        <div
          ref={procedureMenuRef}
          className="membership-add-tier-procedure-menu"
          role="listbox"
          aria-label="Attributed procedures"
          style={{
            top: procedureMenuPosition.top,
            left: procedureMenuPosition.left,
          }}
        >
          {openProcedurePerk.procedureOptions.map((option) => {
            const selected =
              openProcedurePerk.selectedProcedureCode === option.code
            return (
              <button
                key={option.code}
                type="button"
                role="option"
                aria-selected={selected}
                className={`membership-add-tier-procedure-option${
                  selected
                    ? ' membership-add-tier-procedure-option--selected'
                    : ''
                }`}
                onClick={() =>
                  updatePerk(openProcedurePerk.id, {
                    selectedProcedureCode: option.code,
                  })
                }
              >
                <span>
                  {option.code} - {option.description}
                </span>
                {selected ? <SelectCheckIcon aria-hidden /> : null}
              </button>
            )
          })}
        </div>
      ) : null}

      <section className="membership-add-tier-onboarding">
        <h2 className="membership-add-tier-section-title">
          Setup onboarding message
        </h2>
        <div className="membership-add-tier-onboarding-grid">
          <div className="membership-add-tier-message">
            <div className="membership-add-tier-message-head">
              <span className="membership-add-tier-label">Email content:</span>
              {initialTier && editingMessage !== 'email' ? (
                <button
                  type="button"
                  className="membership-add-tier-message-action membership-add-tier-message-action--edit"
                  aria-label="Edit email content"
                  onClick={() => {
                    emailSnapshot.current = emailMessage
                    setEditingMessage('email')
                  }}
                >
                  <EditIcon aria-hidden />
                </button>
              ) : (
                <div className="membership-add-tier-message-actions">
                  <button
                    type="button"
                    className="membership-add-tier-message-action membership-add-tier-message-action--discard"
                    aria-label="Clear email content"
                    onClick={() => {
                      setEmailMessage(
                        initialTier
                          ? emailSnapshot.current
                          : EMAIL_ONBOARDING_MESSAGE,
                      )
                      if (initialTier) setEditingMessage(null)
                    }}
                  >
                    <MultiplyCloseIcon aria-hidden />
                  </button>
                  <button
                    type="button"
                    className="membership-add-tier-message-action membership-add-tier-message-action--confirm"
                    aria-label="Keep email content"
                    onClick={() => {
                      if (initialTier) setEditingMessage(null)
                    }}
                  >
                    <CheckmarkCircleIcon aria-hidden />
                  </button>
                </div>
              )}
            </div>
            <p className="membership-add-tier-sending">
              Sending to: <strong>[email address]</strong>
            </p>
            {initialTier && editingMessage !== 'email' ? (
              <div
                className="membership-add-tier-message-preview"
                dangerouslySetInnerHTML={{ __html: emailMessage }}
              />
            ) : (
              <RichTextEditor
                variant="email-fax"
                value={emailMessage}
                toolbarGroups={DEFAULT_EMAIL_FAX_TOOLBAR}
                bodyActions={['template', 'refine', 'variables']}
                footerActions={['magic', 'emoji', 'attach']}
                onChange={setEmailMessage}
              />
            )}
          </div>
          <div className="membership-add-tier-message">
            <div className="membership-add-tier-message-head">
              <span className="membership-add-tier-label">SMS content:</span>
              {initialTier && editingMessage !== 'sms' ? (
                <button
                  type="button"
                  className="membership-add-tier-message-action membership-add-tier-message-action--edit"
                  aria-label="Edit SMS content"
                  onClick={() => {
                    smsSnapshot.current = smsMessage
                    setEditingMessage('sms')
                  }}
                >
                  <EditIcon aria-hidden />
                </button>
              ) : (
                <div className="membership-add-tier-message-actions">
                  <button
                    type="button"
                    className="membership-add-tier-message-action membership-add-tier-message-action--discard"
                    aria-label="Clear SMS content"
                    onClick={() => {
                      setSmsMessage(
                        initialTier
                          ? smsSnapshot.current
                          : SMS_ONBOARDING_MESSAGE,
                      )
                      if (initialTier) setEditingMessage(null)
                    }}
                  >
                    <MultiplyCloseIcon aria-hidden />
                  </button>
                  <button
                    type="button"
                    className="membership-add-tier-message-action membership-add-tier-message-action--confirm"
                    aria-label="Keep SMS content"
                    onClick={() => {
                      if (initialTier) setEditingMessage(null)
                    }}
                  >
                    <CheckmarkCircleIcon aria-hidden />
                  </button>
                </div>
              )}
            </div>
            <p className="membership-add-tier-sending">
              Sending to: <strong>[contact number]</strong>
            </p>
            {initialTier && editingMessage !== 'sms' ? (
              <div
                className="membership-add-tier-message-preview"
                dangerouslySetInnerHTML={{ __html: smsMessage }}
              />
            ) : (
              <RichTextEditor
                variant="email-fax"
                value={smsMessage}
                toolbarGroups={DEFAULT_EMAIL_FAX_TOOLBAR}
                bodyActions={['template', 'refine', 'variables']}
                footerActions={['magic', 'emoji', 'attach']}
                onChange={setSmsMessage}
              />
            )}
          </div>
        </div>
      </section>
      {attributePerk ? (
        <AttributeProcedureCodesModal
          key={attributePerk.id}
          open
          perkLabel={
            attributePerk.usageLimit
              ? `${attributePerk.usageLimit} ${attributePerk.label}`
              : attributePerk.label
          }
          procedures={attributedProcedureOptions(attributePerk)}
          onClose={() => setAttributePerkId(null)}
          onSave={saveAttributedProcedures}
        />
      ) : null}
      <DeleteMembershipConfirmModal
        open={confirmDeleteTier}
        title="Delete membership tier"
        message={`Are you sure you want to delete “${form.name.trim() || 'this tier'}”?`}
        onClose={() => setConfirmDeleteTier(false)}
        onConfirm={confirmDeleteEditingTier}
      />
      <DeleteMembershipConfirmModal
        open={Boolean(perkToDelete)}
        title="Delete inclusion"
        message={
          perkToDelete?.label.trim()
            ? `Are you sure you want to delete “${perkToDelete.label.trim()}”?`
            : 'Are you sure you want to delete this inclusion?'
        }
        onClose={() => setPerkToDelete(null)}
        onConfirm={() => {
          if (perkToDelete) handleDeletePerk(perkToDelete.id)
        }}
      />
    </div>
  )
}

export default AddMembershipTier
